import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Dumbbell,
  Users,
  Apple,
  TrendingUp,
  Star,
  ChevronRight,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, reviewsRes, teamRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/reviews`),
          axios.get(`${API}/team`),
        ]);
        setServices(servicesRes.data);
        setReviews(reviewsRes.data);
        setTeam(teamRes.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  const serviceIcons = {
    "personal-training": Dumbbell,
    groepslessen: Users,
    transformatie: TrendingUp,
    voedingsbegeleiding: Apple,
  };

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/13951240/pexels-photo-13951240.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          }}
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="caption mb-6 block" data-testid="hero-caption">
              Personal Training & Health Coaching
            </span>
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter"
              data-testid="hero-headline"
            >
              Train Smarter.
              <br />
              <span className="text-red-500">Perform Better.</span>
              <br />
              Live Healthier.
            </h1>
            <p
              className="text-lg md:text-xl text-zinc-400 mb-10 max-w-xl mx-auto"
              data-testid="hero-subheadline"
            >
              Professionele begeleiding en groepslessen in Torhout.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/boeken"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                Start Vandaag
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/diensten"
                data-testid="hero-cta-secondary"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                Bekijk Ons Aanbod
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-red-500 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Intro Section */}
      <section className="section bg-[#0A0A0A]" data-testid="intro-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="caption mb-4 block">Welkom bij Arena Gym</span>
              <h2 className="section-title text-white mb-6 font-['Barlow_Condensed']">
                Jouw Partner in
                <span className="text-red-500"> Gezondheid</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Bij Arena Private Gym geloven we dat iedereen het
                verdient om zich sterk en gezond te voelen. Of je nu wilt
                afvallen, spiermassa opbouwen, of gewoon fitter worden - wij
                staan voor je klaar.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  { id: "pt", text: "Personal training op maat" },
                  { id: "gl", text: "Energieke groepslessen" },
                  { id: "pb", text: "Professionele begeleiding" },
                  { id: "ra", text: "Resultaatgerichte aanpak" },
                  { id: "hc", text: "Hechte community" },
                ].map((item) => (
                  <li key={item.id} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center">
                      <ChevronRight className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="text-zinc-300">{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/over-ons"
                data-testid="intro-learn-more"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Meer over Arena Gym
                <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-sm overflow-hidden border border-zinc-800">
                <img
                  src="/partner-gezondheid.webp"
                  alt="Personal Training"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-red-500 text-black p-6 rounded-sm">
                <div className="text-4xl font-black font-['Barlow_Condensed']">
                  4.9
                </div>
                <div className="text-sm font-bold uppercase tracking-wide">
                  Google Rating
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section
        className="section bg-[#121212]"
        data-testid="services-preview-section"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="caption mb-4 block">Wat wij bieden</span>
            <h2 className="section-title text-white font-['Barlow_Condensed']">
              Onze <span className="text-red-500">Diensten</span>
            </h2>
          </div>

          <div className="bento-grid">
            {services.slice(0, 4).map((service, index) => {
              const Icon = serviceIcons[service.id] || Dumbbell;
              const isLarge = index === 0;
              const isTall = index === 1;

              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`bento-item ${isLarge ? "large" : ""} ${
                    isTall ? "tall" : ""
                  }`}
                  data-testid={`service-card-${service.id}`}
                >
                  <div
                    className="service-card h-full"
                    style={{ backgroundImage: `url('${service.image}')` }}
                  >
                    <div className="service-card-content">
                      <Icon className="w-8 h-8 text-red-500 mb-4" />
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-2 font-['Barlow_Condensed'] uppercase">
                        {service.title}
                      </h3>
                      <p className="text-zinc-400 text-sm mb-4">
                        {service.description}
                      </p>
                      <Link
                        to="/diensten"
                        className="text-red-500 text-sm uppercase tracking-widest font-bold hover:text-red-400 transition-colors inline-flex items-center gap-2"
                      >
                        Meer info
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/diensten"
              data-testid="services-view-all"
              className="btn-primary inline-flex items-center gap-2"
            >
              Bekijk Alle Diensten
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Team Preview */}
      <section className="section bg-[#0A0A0A]" data-testid="team-preview-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="caption mb-4 block">Ons Team</span>
            <h2 className="section-title text-white font-['Barlow_Condensed']">
              Ontmoet Je <span className="text-red-500">Coaches</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="team-card"
                data-testid={`team-card-${member.id}`}
              >
                <img
                  src={member.image}
                  alt={member.naam}
                  className="team-card-image"
                />
                <div className="team-card-overlay">
                  <h3 className="text-2xl font-black text-white font-['Barlow_Condensed'] uppercase">
                    {member.naam}
                  </h3>
                  <p className="text-red-500 text-sm uppercase tracking-wider mb-2">
                    {member.role}
                  </p>
                  <p className="team-card-bio text-zinc-400 text-sm">
                    {member.bio.substring(0, 100)}...
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/team"
              data-testid="team-view-all"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Meer over het Team
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Marquee */}
      <section
        className="section bg-[#121212] overflow-hidden"
        data-testid="reviews-section"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center">
            <span className="caption mb-4 block">Wat onze leden zeggen</span>
            <h2 className="section-title text-white font-['Barlow_Condensed']">
              <span className="text-red-500">4.9</span> Google Rating
            </h2>
          </div>
        </div>

        <div className="marquee py-8">
          <div className="marquee-content">
            {[...reviews, ...reviews].map((review, index) => (
              <div
                key={`${review.id}-${index}`}
                className="flex-shrink-0 w-[350px] bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
                data-testid={`review-card-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].slice(0, review.rating).map((star) => (
                    <Star
                      key={`${review.id}-star-${star}`}
                      className="w-5 h-5 fill-red-500 text-red-500"
                    />
                  ))}
                </div>
                <p className="text-zinc-300 mb-4 italic">"{review.tekst}"</p>
                <p className="text-red-500 font-bold text-sm">
                  — {review.naam}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center">
          <Link
            to="/reviews"
            data-testid="reviews-view-all"
            className="btn-secondary inline-flex items-center gap-2"
          >
            Alle Reviews Bekijken
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Ruimte Verhuur Section */}
      <section
        className="section bg-[#0A0A0A]"
        data-testid="verhuur-section"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="caption mb-4 block">Voor Personal Trainers</span>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
                Huur Onze <span className="text-red-500">Ruimte</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Ben je personal trainer en op zoek naar een professionele locatie 
                om je klanten te trainen? Ons volledig uitgeruste pand in Torhout 
                is beschikbaar voor verhuur aan zelfstandige personal trainers.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  { id: "vu", text: "Volledig uitgeruste trainingsruimte" },
                  { id: "pa", text: "Professionele apparatuur beschikbaar" },
                  { id: "fh", text: "Flexibele huurtijden" },
                  { id: "il", text: "Ideale locatie in Torhout" },
                  { id: "po", text: "Perfecte omgeving voor jouw klanten" },
                ].map((item) => (
                  <li key={item.id} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center">
                      <ChevronRight className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="text-zinc-300">{item.text}</span>
                  </li>
                ))}
              </ul>
              <a
                href="tel:0479380156"
                data-testid="verhuur-cta"
                className="btn-primary inline-flex items-center gap-2"
              >
                Bel Ons: 0479 38 01 56
                <ArrowRight size={18} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="aspect-square rounded-sm overflow-hidden border border-zinc-800 col-span-2">
                  <img
                    src="/pand-yphi.png"
                    alt="Arena Gym Pand - Gym te huur voor personal trainers"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-sm overflow-hidden border border-zinc-800">
                  <img
                    src="/gym-interior-1.png"
                    alt="Squat racks en trainingsruimte"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-sm overflow-hidden border border-zinc-800">
                  <img
                    src="/gym-interior-2.png"
                    alt="Smith machine en kabeltoren"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-sm overflow-hidden border border-zinc-800 col-span-2">
                  <img
                    src="/gym-interior-3.png"
                    alt="Dumbbells en gewichten"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="section bg-[#0A0A0A] relative overflow-hidden"
        data-testid="cta-section"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            background:
              "radial-gradient(circle at center, rgba(217, 249, 157, 0.3) 0%, transparent 70%)",
          }}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center">
            <span className="caption mb-4 block">Klaar om te beginnen?</span>
            <h2 className="section-title text-white mb-6 font-['Barlow_Condensed']">
              Start Jouw{" "}
              <span className="text-red-500">Transformatie</span> Vandaag
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Boek je volledig gratis intake gesprek en ontdek hoe wij je kunnen helpen om
              jouw doelen te bereiken.
            </p>
            <Link
              to="/boeken"
              data-testid="cta-book-intake"
              className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-5"
            >
              Boek Je Intake
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
